<template>
  <div
    class="ds-select-field"
    :class="{ 'ds-select-field--light': light }"
  >
    <TagIcon
      v-if="icon"
      class="ds-select-field__icon w-5 h-5"
      :icon="icon"
      color="mono-medium"
    />
    <ChevronDownIcon class="ds-select-field__arrow" />
    <select
      :id="id"
      class="ds-select-field__select"
      :name="name"
      :class="classes"
      :readonly="readonly"
      :disabled="isDisabled"
      :value="modelValue"
      :aria-invalid="isMarkedAsInvalid || (hasErrors && !val)"
      :aria-label="ariaLabel"
      :data-test-id="dataTestId"
      @input="input($event.target.value)"
      @click="onSelectClick"
      @focus="onSelectFocus"
    >
      <option
        v-if="placeholder"
        value=""
        :disabled="!isOptional"
        selected
      >
        {{ placeholder }}
      </option>
      <slot v-if="options.length === 0" />
      <template v-else-if="hasOptionGroups">
        <optgroup
          v-for="(options, optgroup) in optionsByOptgroup"
          :label="optgroup"
          :key="optgroup"
        >
          <option
            v-for="option in options"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </optgroup>
      </template>
      <template v-else>
        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :class="{ 'hidden': option.hidden }"
        >
          {{ option.label }}
        </option>
      </template>
    </select>
    <FormCaption
      v-if="caption"
      type="info"
      class="mt-1"
    >
      {{ caption }}
    </FormCaption>
    <FormCaption
      v-if="hasErrors && !val"
      type="invalid"
      class="mt-1"
    >
      {{ firstError }}
    </FormCaption>
  </div>
</template>

<script>
import ChevronDownIcon from '@tagvenue/icons/24/bold/arrows/chevron-down.svg';

import TagIcon from '@/components/TagIcon.vue';

import htmlProps from '../htmlProps.js';
import commonProps from './common-props-mixin';
import FormCaption from './FormCaption.vue';
import validation from './validation-mixin';

export default {
  name: 'BaseSelectField',
  // keep input to prevent emitting native event when v-bind=$props is used
  emits: ['change', 'update:modelValue', 'input', 'selectClick', 'selectOpen'],
  mixins: [validation, commonProps, htmlProps],
  components: {
    TagIcon,
    FormCaption,
    ChevronDownIcon,
  },
  props: {
    modelValue: {
      type: [String, Number],
    },
    options: {
      type: [Array, Object],
      default() {
        return [];
      },
    },
    id: {
      type: String,
      default: null,
    },
    placeholderValue: {
      type: [String, Number],
      default: null,
    },
    isOptional: {
      type: Boolean,
      required: false,
      default: false,
    },
    light: {
      type: Boolean,
      default: false,
    },
    ariaLabel: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      val: this.modelValue,
    };
  },
  computed: {
    classes() {
      if (this.placeholder && !this.val && this.val?.toString() !== '0') {
        return 'ds-select-field__select--placeholder';
      }
      if (this.placeholderValue && this.val === this.placeholderValue) {
        return 'ds-select-field__select--placeholder';
      }
      return null;
    },
    hasOptionGroups() {
      return this.options.some((option) => option.optgroup);
    },
    optionsByOptgroup() {
      return this.options.reduce((acc, option) => {
        if (option.optgroup) {
          if (!acc[option.optgroup]) {
            acc[option.optgroup] = [];
          }
          acc[option.optgroup].push(option);
        }
        return acc;
      }, {});
    },
  },
  watch: {
    modelValue(value) {
      this.val = value;
    },
  },
  methods: {
    onSelectClick(e) {
      if (e.offsetY > 0) {
        this.$emit('selectClick');
      }
    },
    onSelectFocus() {
      this.$emit('selectOpen');
    },
    input(val) {
      this.val = val;
      this.$emit('update:modelValue', val); // Don't change the order
      this.$emit('change', val);
    },
  },
};
</script>
